import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useAuth } from "@/lib/auth/useAuth";
import { loginRequest, silentRedirectUri } from "@/lib/auth/msalConfig";
import { createApiClient, type WidgetConfig } from "@/lib/api/chatApi";
import WidgetBubble from "@/components/widget/WidgetBubble";
import { contextoEnlaceValido } from "@/lib/context";

/* ── Types ── */

type PageState =
  | { status: "loading" }
  | { status: "login_required" }
  | { status: "not_found" }
  | { status: "no_access" }
  | { status: "ready"; config: WidgetConfig; agentName: string };

/* ── Component ── */

export default function WidgetPage() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  // Iframe embebido: el redirect de pagina completa no funciona aqui — popup.
  const { isAuthenticated, user, getToken, inProgress } = useAuth({
    redirectUri: silentRedirectUri,
    interaction: "popup",
  });
  const { instance } = useMsal();
  const ssoAttempted = useRef(false);
  const [pageState, setPageState] = useState<PageState>({ status: "loading" });
  const initializedRef = useRef(false);
  const api = useMemo(() => createApiClient(getToken), [getToken]); // Widget: no logout on auth failure (iframe context)

  // Contexto de los parametros de la URL: solo los que declara el widget en su
  // `context_schema`, saneados (lib/context.ts). Se calcula con la config ya
  // cargada; antes no se usa.
  const schema = pageState.status === "ready" ? pageState.config.context_schema : null;
  const context = useMemo(() => contextoEnlaceValido(searchParams, schema), [searchParams, schema]);

  // Force transparent background — override app theme inside iframe
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    document.body.style.minHeight = "auto";
    return () => {
      document.documentElement.style.background = "";
      document.body.style.background = "";
      document.body.style.minHeight = "";
    };
  }, []);

  // Load widget config and verify permissions
  const initialize = useCallback(async () => {
    if (!widgetId || !isAuthenticated || initializedRef.current) return;
    if (inProgress !== InteractionStatus.None) return;

    initializedRef.current = true;

    try {
      // Fetch widget config (public endpoint)
      let config: WidgetConfig;
      try {
        config = await api.getWidgetConfig(widgetId);
      } catch {
        setPageState({ status: "not_found" });
        return;
      }

      const agentName = config.agent_description || config.agent_id;

      setPageState({ status: "ready", config, agentName });
    } catch (err) {
      console.error("Widget init error:", err);
      setPageState({ status: "not_found" });
    }
  }, [widgetId, isAuthenticated, api, inProgress]);

  // Try SSO silent first — reuses existing Microsoft session without interaction
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None && !ssoAttempted.current) {
      ssoAttempted.current = true;
      instance
        .ssoSilent({ ...loginRequest, redirectUri: silentRedirectUri })
        .then((response) => {
          instance.setActiveAccount(response.account);
        })
        .catch(() => {
          setPageState({ status: "login_required" });
        });
      return;
    }

    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      setPageState({ status: "login_required" });
      return;
    }

    if (isAuthenticated) {
      initialize();
    }
  }, [isAuthenticated, inProgress, initialize, instance]);

  // Styles
  const containerStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "transparent",
    fontFamily: "'Poppins', -apple-system, system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    pointerEvents: "none",
  };

  const centerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    pointerEvents: "auto",
  };

  if (pageState.status === "loading") {
    return <div style={containerStyle} />;
  }

  if (pageState.status === "login_required") {
    return (
      <div style={containerStyle}>
        <div style={centerStyle}>
          <div style={{ textAlign: "center" }}>
            <p style={{ color: "#53565A", fontSize: 14, marginBottom: 12 }}>
              {t("widget.login_required")}
            </p>
            <button
              onClick={() => instance.loginPopup(loginRequest).catch(console.error)}
              style={{
                background: "#C8102E",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("widget.login")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pageState.status === "not_found") {
    console.warn("[Fonestar Widget] Widget not found:", widgetId);
    return <div style={containerStyle} />;
  }

  if (pageState.status === "no_access") {
    console.warn("[Fonestar Widget] User does not have access to this agent");
    return <div style={containerStyle} />;
  }

  const { config, agentName } = pageState;

  return (
    <div style={containerStyle}>
      <WidgetBubble
        api={api}
        agentId={config.agent_id}
        agentName={agentName}
        widgetUid={config.widget_uid}
        context={context}
        contextPromptTemplate={config.context_prompt_template}
        threadTtlHours={config.thread_ttl_hours}
        userEmail={user?.email ?? ""}
      />
    </div>
  );
}
